import { BadgeCheckIcon } from "@heroicons/react/solid";
import { find, get, isEmpty, isNaN, keys, mean } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import Hex from "../../../../components/Hex";
import HotspotActionsButton from "../../../../components/HotspotActionsButton";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import FilterLabels from "../../../../components/labels/FilterLabels";
import VirtualScroll from "../../../../components/VirtualScroll";
import { useFilteredList } from "../../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../../context/filteredList/FilteredListProvider";
import { useHotspots } from "../../../../context/hotspots";
import { useInstalls } from "../../../../context/installs";
import { useLabels } from "../../../../context/labels";
import { useLoading } from "../../../../context/loading";
import { LoadingKey } from "../../../../context/loading/LoadingProvider";
import { useTransmitScales } from "../../../../context/transmitScales";
import { useViewport } from "../../../../context/viewport";
import classNames from "../../../../utils/classNames";
import { avatarsApi, breakpoints, Entity } from "../../../../utils/constants";
import {
  classificationColorMap,
  formatHotspotName,
  formatHotspotPlace,
  relativePerformanceClassifications
} from "../../../../utils/hotspotUtils";
import { Hotspot, PhpHotspot } from "../../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../../utils/useFilteredLabels";
import useMapConfig, {
  relativePerformanceMetrics
} from "../../../../utils/useMapConfig";
import useSideMenu from "../useSideMenu";

interface Props {}

const NearbyHotspotsList: React.FC<Props> = () => {
  const location = useLocation();
  const history = useHistory();
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { getIsReady } = useLoading();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const { walletVerificationStatusMap } = useHotspots();
  const { toggleInstallSidebar } = useInstalls();
  const {
    filteredList,
    smartLabelFilters,
    histogramBucketIndexMap,
    getClassificationValue,
    renderClassificationValue
  } = useFilteredList();
  const { labels } = useLabels();
  const { isTransmitScaleActive } = useTransmitScales();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );

  const filteredHotspotLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapHotspots],
        labels,
        smartLabelFilters[Entity.hotspot],
        Entity.hotspot
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredHotspots = useMemo(
    () =>
      filteredList[Entity.hotspot].sort((a, b) =>
        getClassificationValue(b, pointColorMetricIteratee) <
        getClassificationValue(a, pointColorMetricIteratee)
          ? -1
          : 1
      ),
    [filteredList, getClassificationValue, pointColorMetricIteratee]
  );

  const detailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(location.pathname, {
    path: [
      "/hotspots/makers/:id/:section/:subsection",
      "/hotspots/makers/:id/:section",
      "/hotspots/:id/:section/:subsection",
      "/hotspots/:id/:section"
    ]
  });

  const toggleHotspot = useCallback(
    (hotspot: Hotspot) => {
      if (isMobile) toggleSideMenu();

      const {
        id,
        section,
        subsection
      }: {
        id?: string;
        section?: string;
        subsection?: string;
      } = detailsPageMatch?.params || {};

      if (!hotspot || hotspot.id === id) {
        history.push("/hotspots");
      } else {
        let sectionPath = "status";

        if (section) {
          sectionPath = subsection ? `${section}/${subsection}` : section;
        }

        history.push(`/hotspots/${hotspot.id}/${sectionPath}`);
      }
    },
    [detailsPageMatch?.params, history, isMobile, toggleSideMenu]
  );

  const renderHotspot = useCallback(
    (index: number) => {
      const hotspot = filteredHotspots[index];

      const bucketIndex = get(
        histogramBucketIndexMap,
        [hotspot.id, pointColorMetricIteratee],
        0
      );

      let classification = relativePerformanceClassifications[bucketIndex];

      if (pointColorMetricIteratee === "reward_scale") {
        if (!isTransmitScaleActive(hotspot.id)) {
          classification = "disabled";
        }
      }

      return (
        <div
          key={hotspot.id}
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-hotspotty-400 border-b border-gray-200 dark:border-gray-500 dark:ring-white",
            detailsPageMatch?.params?.id === hotspot.id && "ring-2"
          )}
          onClick={() => toggleHotspot(hotspot)}
        >
          <div className="flex-shrink-0">
            <img
              className="h-10 w-10 rounded-full"
              src={stringifyUrl({
                url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                query: { w: 40, h: 40 }
              })}
              alt="Hotspot avatar"
            />
          </div>
          <div className="flex-1 min-w-0 focus:outline-none">
            <span className="absolute inset-0" aria-hidden="true" />
            <div className="flex items-center">
              {((hotspot as PhpHotspot).verified_by ||
                !!walletVerificationStatusMap[hotspot.owner]) && (
                <div className="w-5 h-5 mr-1">
                  <BadgeCheckIcon className="mr-1 h-5 w-5 text-green-400" />
                </div>
              )}
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate items-center">
                {" "}
                {formatHotspotName(hotspot)}{" "}
              </p>
            </div>
            <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
              {formatHotspotPlace(hotspot.place)}
            </p>
            <DisplayLabels
              linkedUuid={hotspot.id}
              contextWalletAddresses={[hotspot.owner]}
              size="small"
            />
          </div>
          <div className="text-sm text-gray-500">
            {classification !== "disabled" &&
              !["status", "verified"].includes(pointColorMetricIteratee) && (
                <div className="flex items-center">
                  <span className="flex items-center justify-center">
                    <Hex
                      fillColor={classificationColorMap[classification]}
                      width={10.5}
                      height={12}
                    />
                  </span>

                  <p className="ml-2">
                    {renderClassificationValue(
                      getClassificationValue(hotspot, pointColorMetricIteratee),
                      pointColorMetricIteratee
                    )}
                  </p>
                </div>
              )}
          </div>
          <HotspotActionsButton
            hotspot={hotspot}
            toggleAddInstallForm={() =>
              toggleInstallSidebar({
                uuid: "",
                created_by: "",
                created_at: "",
                updated_by: "",
                updated_at: "",
                hotspot_uuid: hotspot.id,
                location_uuid: "",
                installed_at: "",
                uninstalled_at: "",
                commissions: []
              })
            }
          />
        </div>
      );
    },
    [
      filteredHotspots,
      histogramBucketIndexMap,
      pointColorMetricIteratee,
      isTransmitScaleActive,
      detailsPageMatch?.params?.id,
      walletVerificationStatusMap,
      renderClassificationValue,
      getClassificationValue,
      toggleHotspot,
      toggleInstallSidebar
    ]
  );

  const avgClassificationValue = useMemo(
    () =>
      mean(
        filteredHotspots
          .map((hotspot) =>
            getClassificationValue(hotspot, pointColorMetricIteratee)
          )
          .filter((value) => value > 0)
      ),
    [filteredHotspots, getClassificationValue, pointColorMetricIteratee]
  );

  const isLoadingHotspots = useMemo(
    () => !getIsReady([LoadingKey.hotspotsAroundLocations]),
    [getIsReady]
  );

  const renderContent = useMemo(() => {
    if (isLoadingHotspots) {
      return <CenteredLoading className="h-full" />;
    }

    if (isEmpty(filteredHotspots)) {
      return (
        <div className="px-6 pt-6 pb-4">
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            No hotspots to show.
          </p>
        </div>
      );
    }

    return (
      <VirtualScroll
        className="overflow-y-auto"
        minItemHeight={80}
        totalLength={filteredHotspots.length}
        renderItem={renderHotspot}
      />
    );
  }, [filteredHotspots, isLoadingHotspots, renderHotspot]);

  const renderAvgClassification = useMemo(
    () => (
      <>
        {!isNaN(avgClassificationValue) && !isLoadingHotspots && (
          <div className="text-xs">
            Avg{" "}
            {find(relativePerformanceMetrics, {
              key: pointColorMetricIteratee
            })?.title || "value"}
            :{" "}
            {renderClassificationValue(
              avgClassificationValue,
              pointColorMetricIteratee
            )}
          </div>
        )}
      </>
    ),
    [
      avgClassificationValue,
      isLoadingHotspots,
      pointColorMetricIteratee,
      renderClassificationValue
    ]
  );

  const renderFilteredLabels = useMemo(
    () => (
      <FilterLabels
        entity={Entity.hotspot}
        filteredLabels={filteredHotspotLabels}
        withWalletLabels
        smartLabels={keys(smartLabelFilters[Entity.hotspot]).map((key) =>
          getSmartLabelFromKey(key, Entity.hotspot)
        )}
        filterKey={LabelFilterKey.mapHotspots}
      />
    ),
    [filteredHotspotLabels, smartLabelFilters]
  );

  return (
    <>
      <div className="px-4 pb-2">{renderFilteredLabels}</div>

      <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
        <div className="text-sm font-medium">
          {(isLoadingHotspots ? 0 : filteredHotspots.length).toLocaleString(
            undefined
          )}{" "}
          hotspot
          {(filteredHotspots.length !== 1 || isLoadingHotspots) && "s"}
        </div>
        {renderAvgClassification}
      </div>

      {renderContent}
    </>
  );
};

export default NearbyHotspotsList;
